import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { seoArticles } from '@/mocks/articles';

const siteUrl: string = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Artykuł nie znaleziony</h1>
          <Link to="/" className="inline-block mt-6 px-6 py-3 bg-primary-500 text-white rounded-full font-label font-bold text-sm cursor-pointer hover:bg-primary-600 transition-all">
            Wróć do Strony Głównej
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const contentHtml = article.content
    .replace(/^### (.+)$/gm, '<h3 class="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-heading text-3xl font-bold text-foreground-950 mt-12 mb-5">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-foreground-900">$1</strong>')
    .replace(/^- (.+)$/gm, '<li class="ml-5 list-disc text-foreground-700 leading-relaxed mb-1">$1</li>')
    .replace(/(\d\. .+)/g, '<p class="text-foreground-700 leading-relaxed mb-2 font-label font-bold">$1</p>')
    .split('\n\n')
    .filter((p) => p.trim())
    .map((p) => {
      if (p.startsWith('<h2') || p.startsWith('<h3') || p.startsWith('<li') || p.startsWith('<p class="text-foreground-700 leading-relaxed mb-2')) {
        return p;
      }
      return `<p class="text-foreground-700 leading-relaxed mb-5 text-base">${p}</p>`;
    })
    .join('\n');

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    author: {
      '@type': 'Organization',
      name: 'Zespół KakobuySpreadsheet',
    },
    datePublished: '2025-07-07',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/artykul/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuySpreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    image: 'https://readdy.ai/api/search-image?query=Young%20creative%20professional%20sitting%20at%20a%20warm%20walnut%20wooden%20desk%20working%20on%20a%20laptop%20displaying%20a%20product%20spreadsheet%20grid%2C%20surrounded%20by%20curated%20fashion%20items%20including%20sneakers%20sunglasses%20and%20a%20leather%20bag%2C%20soft%20window%20light%20from%20the%20left%2C%20cozy%20home%20office%20with%20potted%20monstera%20plant%2C%20editorial%20lifestyle%20photography%2C%20warm%20earth%20tones&width=1600&height=800&seq=article-hero-kakobuy-v2&orientation=landscape',
    inLanguage: 'pl',
  };

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-500 text-sm font-label hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              Wróć do Strony Głównej
            </Link>
          </div>

          <span className="inline-block bg-accent-100 text-accent-700 text-xs font-label font-bold px-3 py-1.5 rounded-full mb-4">
            {article.category}
          </span>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-3 mt-5 text-foreground-400 text-sm">
            <span>Autor: Zespół KakobuySpreadsheet</span>
            <span>•</span>
            <span>Aktualizacja: {new Date().toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>

          <img
            src="https://readdy.ai/api/search-image?query=Young%20creative%20professional%20sitting%20at%20a%20warm%20walnut%20wooden%20desk%20working%20on%20a%20laptop%20displaying%20a%20product%20spreadsheet%20grid%2C%20surrounded%20by%20curated%20fashion%20items%20including%20sneakers%20sunglasses%20and%20a%20leather%20bag%2C%20soft%20window%20light%20from%20the%20left%2C%20cozy%20home%20office%20with%20potted%20monstera%20plant%2C%20editorial%20lifestyle%20photography%2C%20warm%20earth%20tones%2C%20no%20text&width=1600&height=800&seq=article-hero-kakobuy-v2&orientation=landscape"
            alt={`Kakobuy Spreadsheet — ${article.title}`}
            title={`Kakobuy Spreadsheet — ${article.title}`}
            className="w-full rounded-lg mt-8 mb-10 object-cover"
          />

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          <div className="mt-12 p-6 bg-background-100 rounded-lg">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">Podsumowanie</h3>
            <p className="text-foreground-600 text-sm leading-relaxed mb-4">
              Mamy nadzieję, że ten przewodnik pomógł Ci lepiej zrozumieć, jak działa Kakobuy Spreadsheet. Zachęcamy do eksplorowania wszystkich kategorii produktów dostępnych na stronie głównej.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-label font-bold text-sm rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line"></i>
                </span>
                Wróć do Strony Głównej
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground-900 text-white font-label font-bold text-sm rounded-full whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-all"
              >
                Odwiedź Kaspreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </span>
              </a>
            </div>
          </div>

          <nav className="mt-6 flex items-center gap-2 text-sm text-foreground-400">
            <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">Strona Główna</Link>
            <span>/</span>
            <span className="text-foreground-700">{article.title}</span>
          </nav>
        </div>
      </article>

      <Footer />
    </main>
  );
}